// Axios API Client mit JWT Interceptors und Multi-Tenant Support

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5247/api';

/**
 * Extrahiert den Tenant-Code aus der aktuellen URL (Subdomain)
 * Beispiel: scl-app.patzwahl.de -> "scl"
 */
const getTenantCodeFromUrl = (): string | null => {
  const hostname = window.location.hostname;
  // Pattern: {code}-app.domain.tld oder {code}-coaches.domain.tld
  const match = hostname.match(/^([a-z0-9]+)-(?:app|coaches)\./i);
  if (match) {
    return match[1].toLowerCase();
  }
  return null;
};

/**
 * Holt den gespeicherten Tenant-Code aus localStorage
 * (für manuell gewählte Tenants oder localhost-Entwicklung)
 */
const getStoredTenantCode = (): string | null => {
  return localStorage.getItem('tenantCode');
};

/**
 * Speichert den aktuellen Tenant-Code
 */
export const setTenantCode = (code: string | null): void => {
  if (code) {
    localStorage.setItem('tenantCode', code);
  } else {
    localStorage.removeItem('tenantCode');
  }
};

/**
 * Ermittelt den aktuellen Tenant-Code
 * Priorität: 1. URL (Subdomain), 2. localStorage
 */
export const getCurrentTenantCode = (): string | null => {
  return getTenantCodeFromUrl() || getStoredTenantCode();
};

// Axios Instance erstellen
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor: Access Token und Tenant-Code zu jedem Request hinzufügen
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Access Token hinzufügen
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Tenant-Code hinzufügen
    const tenantCode = getCurrentTenantCode();
    if (tenantCode && config.headers) {
      config.headers['X-Tenant-Code'] = tenantCode;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Automatisches Token Refresh bei 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Token abgelaufen? -> Versuche Refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Token Refresh Request (ohne Interceptor!)
        const tenantCode = getCurrentTenantCode();
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refreshToken },
          {
            headers: tenantCode ? { 'X-Tenant-Code': tenantCode } : {}
          }
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Neue Tokens speichern
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Original Request mit neuem Token wiederholen
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh fehlgeschlagen -> Logout (nur Auth-Keys entfernen, Theme etc. behalten).
        // deviceTrustToken bewusst NICHT löschen: er soll über abgelaufene Sessions hinweg
        // bestehen bleiben, damit beim nächsten Login 2FA übersprungen werden kann
        // (konsistent zu authService.clearTokens). Sonst käme trotz "Diesem Gerät vertrauen"
        // bei jeder abgelaufenen Session erneut 2FA.
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tenantCode');
        localStorage.removeItem('currentTenant');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
